
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    destination: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataObj = new FormData(formElement);
      const urlEncodedData = new URLSearchParams();
      formDataObj.forEach((value, key) => {
        urlEncodedData.append(key, value.toString());
      });

      await fetch('https://readdy.ai/api/form/d60nknscm4ll99gup8q0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', destination: '', message: '' });

      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } catch {
      setIsSubmitting(false);
      setSubmitStatus('error');
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const contactMethods = [
    {
      icon: 'ri-mail-send-line',
      title: 'Email Us',
      info: 'hello@travelguide.com',
      description: 'We reply within 4 hours',
    },
    {
      icon: 'ri-phone-line',
      title: 'Call Us',
      info: '+1 (234) 567-890',
      description: 'Mon-Fri, 9am-6pm EST',
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Visit Us',
      info: '123 Travel Street, NYC',
      description: 'Open for appointments',
    },
  ];

  const destinations = [
    'Santorini, Greece',
    'Bali, Indonesia',
    'Kyoto, Japan',
    'Iceland',
    'Morocco',
    'Peru',
    'Other',
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <div className="relative h-[60vh] w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/71332048af196345e3a74d4eb3804d9a.jpg"
            alt="Contact Us"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50" />
        </div>

        <div className="relative h-full flex flex-col items-center justify-center text-white px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <span className="text-sm uppercase tracking-[2px] text-terracotta font-semibold">Get In Touch</span>
            <h1 className="text-5xl md:text-7xl font-bold mt-4 mb-6">
              Let's Plan Your<br />Next Adventure
            </h1>
            <p className="text-xl md:text-2xl max-w-2xl mx-auto leading-relaxed">
              Questions, ideas, or dream destinations? We're here to help make your travel dreams a reality.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Contact Methods */}
      <div className="max-w-[1600px] mx-auto px-4 -mt-16 relative z-10">
        <div className="grid md:grid-cols-3 gap-6">
          {contactMethods.map((method, index) => (
            <motion.div
              key={method.title}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="w-16 h-16 bg-[#C85A3A]/10 rounded-2xl flex items-center justify-center mb-6">
                <i className={`${method.icon} text-3xl text-[#C85A3A]`} />
              </div>
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-2">{method.title}</h3>
              <p className="text-lg font-semibold text-[#C85A3A] mb-1">{method.info}</p>
              <p className="text-gray-500">{method.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-4 py-24">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-sm uppercase tracking-[2px] text-[#C85A3A] font-semibold">Send a Message</span>
            <h2 className="text-4xl font-bold text-[#1A1A1A] mt-4 mb-8">
              We'd Love to Hear From You
            </h2>

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-[#1A1A1A] mb-3">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-4 bg-[#FAFAF8] border-2 border-gray-200 rounded-xl text-[#1A1A1A] focus:border-[#C85A3A] focus:outline-none transition-all duration-300"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#1A1A1A] mb-3">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-4 bg-[#FAFAF8] border-2 border-gray-200 rounded-xl text-[#1A1A1A] focus:border-[#C85A3A] focus:outline-none transition-all duration-300"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-[#1A1A1A] mb-3">
                    Subject
                  </label>
                  <div className="relative">
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-4 bg-[#FAFAF8] border-2 border-gray-200 rounded-xl text-[#1A1A1A] focus:border-[#C85A3A] focus:outline-none appearance-none cursor-pointer transition-all duration-300"
                    >
                      <option value="">Select a topic</option>
                      <option value="trip-planning">Trip Planning</option>
                      <option value="collaboration">Collaboration</option>
                      <option value="press">Press & Media</option>
                      <option value="feedback">Feedback</option>
                      <option value="other">Other</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label htmlFor="destination" className="block text-sm font-semibold text-[#1A1A1A] mb-3">
                    Dream Destination
                  </label>
                  <div className="relative">
                    <select
                      id="destination"
                      name="destination"
                      value={formData.destination}
                      onChange={handleChange}
                      className="w-full px-4 py-4 bg-[#FAFAF8] border-2 border-gray-200 rounded-xl text-[#1A1A1A] focus:border-[#C85A3A] focus:outline-none appearance-none cursor-pointer transition-all duration-300"
                    >
                      <option value="">Where would you like to go?</option>
                      {destinations.map((dest) => (
                        <option key={dest} value={dest}>{dest}</option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-[#1A1A1A] mb-3">
                  Your Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-4 bg-[#FAFAF8] border-2 border-gray-200 rounded-xl text-[#1A1A1A] focus:border-[#C85A3A] focus:outline-none resize-none transition-all duration-300"
                  placeholder="Tell us about your travel plans or questions..."
                />
                <div className="flex justify-end mt-2">
                  <span className="text-sm text-gray-500">{formData.message.length}/500</span>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#C85A3A] text-white py-4 rounded-xl font-semibold text-lg hover:bg-[#B04A2A] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 whitespace-nowrap shadow-lg hover:shadow-xl"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-xl" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <i className="ri-send-plane-line text-xl" />
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#6B8E6F]/10 border-2 border-[#6B8E6F] text-[#6B8E6F] px-6 py-4 rounded-xl flex items-center gap-4"
                >
                  <div className="w-10 h-10 bg-[#6B8E6F] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-xl text-white" />
                  </div>
                  <div>
                    <p className="font-bold">Message Sent Successfully!</p>
                    <p className="text-sm opacity-80">We'll get back to you within 4 hours.</p>
                  </div>
                </motion.div>
              )}

              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 border-2 border-red-300 text-red-600 px-6 py-4 rounded-xl flex items-center gap-4"
                >
                  <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-line text-xl text-white" />
                  </div>
                  <div>
                    <p className="font-bold">Something went wrong</p>
                    <p className="text-sm opacity-80">Please try again or email us directly.</p>
                  </div>
                </motion.div>
              )}
            </form>
          </motion.div>

          {/* Right Side Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Image */}
            <div className="relative h-80 rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://readdy.ai/api/search-image?query=travel%20planning%20session%20with%20map%20coffee%20and%20notebook%20on%20wooden%20table%20warm%20cozy%20atmosphere%20natural%20lighting%20minimalist%20aesthetic&width=800&height=500&seq=contact-side-v2&orientation=landscape"
                alt="Travel Planning"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute bottom-6 left-6 text-white">
                <p className="text-2xl font-bold">Ready to Explore?</p>
                <p className="text-white/80">Let's create your perfect itinerary</p>
              </div>
            </div>

            {/* Stats */}
            <div className="bg-[#FAFAF8] rounded-2xl p-8">
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-6">Why Travelers Trust Us</h3>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-[#C85A3A] mb-2">150+</div>
                  <p className="text-gray-600">Destinations</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-[#C85A3A] mb-2">50K+</div>
                  <p className="text-gray-600">Happy Travelers</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-[#C85A3A] mb-2">4.9</div>
                  <p className="text-gray-600">Average Rating</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-[#C85A3A] mb-2">4hr</div>
                  <p className="text-gray-600">Response Time</p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="bg-white border-2 border-gray-100 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-6">Follow Our Adventures</h3>
              <div className="flex gap-4">
                {[
                  { icon: 'ri-instagram-line', label: 'Instagram' },
                  { icon: 'ri-youtube-line', label: 'YouTube' },
                  { icon: 'ri-twitter-x-line', label: 'Twitter' },
                  { icon: 'ri-pinterest-line', label: 'Pinterest' },
                ].map((social) => (
                  <a
                    key={social.label}
                    href="#"
                    className="w-14 h-14 flex items-center justify-center bg-[#FAFAF8] rounded-xl hover:bg-[#C85A3A] hover:text-white text-[#1A1A1A] transition-all duration-300 cursor-pointer"
                    aria-label={social.label}
                  >
                    <i className={`${social.icon} text-2xl`} />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Map Section */}
      <div className="bg-[#FAFAF8] py-24">
        <div className="max-w-[1600px] mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-[2px] text-[#C85A3A] font-semibold">Our Location</span>
            <h2 className="text-4xl font-bold text-[#1A1A1A] mt-4">Visit Our Office</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Stop by our NYC headquarters for a coffee and chat about your next adventure
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Office Info Cards */}
            <div className="lg:col-span-1 space-y-6">
              {[
                {
                  city: 'New York',
                  address: '123 Travel Street, NY 10001',
                  hours: 'Mon-Fri: 9am - 6pm',
                  icon: 'ri-building-line',
                },
                {
                  city: 'London',
                  address: '45 Explorer Lane, W1 2AB',
                  hours: 'Mon-Fri: 9am - 5pm GMT',
                  icon: 'ri-building-2-line',
                },
                {
                  city: 'Tokyo',
                  address: '7-8 Shibuya, Tokyo 150-0002',
                  hours: 'Mon-Fri: 10am - 7pm JST',
                  icon: 'ri-building-4-line',
                },
              ].map((office) => (
                <div
                  key={office.city}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#C85A3A]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className={`${office.icon} text-2xl text-[#C85A3A]`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-[#1A1A1A]">{office.city}</h3>
                      <p className="text-gray-600 text-sm mt-1">{office.address}</p>
                      <p className="text-[#6B8E6F] text-sm mt-2 flex items-center gap-2">
                        <i className="ri-time-line" />
                        {office.hours}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Map */}
            <div className="lg:col-span-2 bg-white rounded-2xl p-4 shadow-xl overflow-hidden">
              <div className="w-full h-96 rounded-xl overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.9476519598093!2d-73.99185368459395!3d40.74844097932847!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c259a9b3117469%3A0xd134e199a405a163!2sEmpire%20State%20Building!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Office Location"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative px-4 py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20tropical%20beach%20with%20turquoise%20water%20and%20white%20sand%20pristine%20paradise%20destination%20cinematic%20travel%20photography&width=1920&height=600&seq=contact-cta-v2&orientation=landscape"
            alt="Start Your Journey"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#C85A3A]/90 to-[#B04A2A]/90" />
        </div>

        <div className="relative max-w-4xl mx-auto text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Start Exploring?
            </h2>
            <p className="text-xl mb-10 leading-relaxed opacity-90">
              Browse our curated collection of destinations and find your next adventure
            </p>
            <a
              href="/destinations"
              className="inline-block bg-white text-[#C85A3A] px-12 py-4 rounded-full font-semibold text-lg hover:bg-[#FAFAF8] transition-all duration-300 transform hover:scale-105 shadow-2xl cursor-pointer whitespace-nowrap"
            >
              Explore Destinations
            </a>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ContactPage;
